import React, { useEffect, useMemo, useState } from 'react';
import { ArrowPathIcon, CheckIcon } from '@heroicons/react/24/outline';

import internalAuditApi from '../../api/internalauditApi';
import DataTable from '../../components/ui/DataTable';
import ActionToolbar from '../../components/ui/ActionToolbar';
import IconActionButton from '../../components/ui/IconActionButton';
import { Button } from '../../components/ui/Button';

const InternalAuditAccessManagementPage = () => {
  const [rows, setRows] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const loadUsers = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await internalAuditApi.getModuleAccessUsers();
      const users = response?.data || [];
      setRows(users);
      setSelectedIds(
        users
          .filter((item) => item.can_access_internal_audit)
          .map((item) => item.id)
      );
    } catch (err) {
      setError(err.message || 'Kullanıcı yetkileri yüklenemedi.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadUsers();
  }, []);

  const toggleUser = (userId) => {
    setSelectedIds((prev) => (
      prev.includes(userId)
        ? prev.filter((id) => id !== userId)
        : [...prev, userId]
    ));
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      await internalAuditApi.updateModuleAccessUsers(selectedIds);
      await loadUsers();
    } catch (err) {
      setError(err.message || 'Yetki güncellemesi başarısız.');
    } finally {
      setSaving(false);
    }
  };

  const columns = useMemo(() => [
    {
      header: 'Erişim',
      accessorKey: 'can_access_internal_audit',
      cell: ({ row }) => (
        <input
          type="checkbox"
          checked={selectedIds.includes(row.original.id)}
          onChange={() => toggleUser(row.original.id)}
          className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
        />
      ),
    },
    {
      header: 'Kullanıcı',
      accessorKey: 'full_name',
      cell: ({ row }) => (
        <div className="flex flex-col">
          <span className="font-medium text-gray-900 dark:text-white">{row.original.full_name}</span>
          <span className="text-xs text-gray-500 dark:text-gray-400">{row.original.email}</span>
        </div>
      ),
    },
    {
      header: 'Otel',
      accessorKey: 'hotel_name',
      cell: ({ getValue }) => getValue() || '-',
    },
    {
      header: 'Departman',
      accessorKey: 'department_name',
      cell: ({ getValue }) => getValue() || '-',
    },
    {
      header: 'Rol',
      accessorKey: 'role_name',
      cell: ({ getValue }) => getValue() || '-',
    },
    {
      header: 'Durum',
      accessorKey: 'is_active',
      cell: ({ getValue }) => (
        <span className={`inline-flex rounded-full px-2 py-0.5 text-[10px] font-medium ${getValue() ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300' : 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300'}`}>
          {getValue() ? 'Aktif' : 'Pasif'}
        </span>
      ),
    },
  ], [selectedIds]);

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <div className="px-6 py-6 space-y-4">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">Denetim Modülü Yetkilendirme</h1>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Denetim Modülü&apos;ne erişecek kullanıcıları bu ekrandan seçip kaydedin.
          </p>
        </div>

        {error ? (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded dark:bg-red-900/20 dark:border-red-800 dark:text-red-300">
            {error}
          </div>
        ) : null}

        <ActionToolbar>
          <IconActionButton tone="hero" icon={ArrowPathIcon} onClick={loadUsers} title="Yenile" />
        </ActionToolbar>

        <div className="flex items-center justify-between rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 px-4 py-3">
          <div className="text-sm text-gray-600 dark:text-gray-300">
            Seçili kullanıcı sayısı: <span className="font-semibold text-gray-900 dark:text-white">{selectedIds.length}</span>
          </div>
          <Button type="button" onClick={handleSave} className="bg-blue-600 hover:bg-blue-700 text-white" disabled={saving}>
            <CheckIcon className="h-4 w-4" />
            {saving ? 'Kaydediliyor...' : 'Yetkileri Kaydet'}
          </Button>
        </div>

        <DataTable
          data={rows}
          columns={columns}
          isLoading={loading}
          showSearch
        />
      </div>
    </div>
  );
};

export default InternalAuditAccessManagementPage;
